import React, {
  useMemo,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
} from 'react'
import ReactECharts from 'echarts-for-react'
import * as echarts from 'echarts'
import { useTheme } from 'styled-components'
import { ValuationSource } from '@popety-io/types'

export interface ValuationChartData {
  source: ValuationSource
  name: string
  value: number
  confidenceLow?: number
  confidenceHigh?: number
  color: string
  area?: number
}

export interface FinalValuationData {
  value?: number
  range?: {
    min?: number
    max?: number
  }
}

type TranslationFn = (key: string, options?: Record<string, unknown>) => string

export interface ValuationComparisonChartProps {
  data: ValuationChartData[]
  currency?: string
  finalValuation?: FinalValuationData
  finalValuationLabel?: string
  height?: number | string
  rangeLabel?: string
  t?: TranslationFn
  /**
   * When false, hides all `/m²` values from the tooltip.
   * Defaults to true for backwards-compatibility.
   */
  showPricePerSqm?: boolean
}

export interface ValuationComparisonChartRef {
  exportChart: () => string | null
}

const formatCurrency = (value: number, currency: string): string => {
  return `${currency} ${value.toLocaleString('de-CH')}`
}

// Helper to wrap text into multiple lines
const wrapLabel = (text: string, maxLineLength: number = 14): string => {
  const words = text.split(' ')
  const lines: string[] = []
  let currentLine = ''

  for (const word of words) {
    if (currentLine && (currentLine + ' ' + word).length > maxLineLength) {
      lines.push(currentLine)
      currentLine = word
    } else {
      currentLine = currentLine ? currentLine + ' ' + word : word
    }
  }
  if (currentLine) lines.push(currentLine)

  // Truncate to 2 lines max, with ellipsis if needed
  return lines
    .slice(0, 2)
    .map((l, i) => {
      if (i === 1 && lines.length > 2) {
        return l.length > maxLineLength - 3
          ? l.slice(0, maxLineLength - 3) + '...'
          : l + '...'
      }
      return l.length > maxLineLength
        ? l.slice(0, maxLineLength - 3) + '...'
        : l
    })
    .join('\n')
}

export const ValuationComparisonChart = forwardRef<
  ValuationComparisonChartRef,
  ValuationComparisonChartProps
>(
  (
    {
      data,
      currency = 'CHF',
      finalValuation,
      finalValuationLabel,
      height = '300px',
      rangeLabel,
      t = (key) => key,
      showPricePerSqm = true,
    },
    ref
  ) => {
    const theme = useTheme()
    const primaryColor = theme.colors.primary
    const chartInstance = useRef<echarts.ECharts | null>(null)

    const [isMobile, setIsMobile] = useState(
      typeof window !== 'undefined' && window.innerWidth <= 768
    )

    useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth <= 768)
      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }, [])

    useImperativeHandle(ref, () => ({
      exportChart: () => {
        if (chartInstance.current) {
          return chartInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#fff',
          })
        }
        return null
      },
    }))

    const option = useMemo(() => {
      // Calculate domain for better y-axis range
      // Include final valuation values in the range calculation
      const finalValues = finalValuation?.value
        ? [
            finalValuation.value,
            finalValuation.range?.min,
            finalValuation.range?.max,
          ].filter((v): v is number => v !== undefined)
        : []

      const allValues = [
        ...data.flatMap((d) => [
          d.confidenceLow || d.value,
          d.value,
          d.confidenceHigh || d.value,
        ]),
        ...finalValues,
      ]
      const minValue = Math.min(...allValues)
      const maxValue = Math.max(...allValues)
      const padding = (maxValue - minValue) * 0.25 // Increased from 0.15 to 0.25 for more space

      return {
        grid: {
          left: isMobile ? 40 : 60,
          right: isMobile ? 16 : finalValuation?.value ? 120 : 30,
          top: 30,
          bottom: isMobile ? 60 : 80,
          containLabel: false,
        },
        xAxis: {
          type: 'category',
          data: data.map((d) => d.name),
          axisLine: {
            lineStyle: {
              color: '#e0e0e0',
            },
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            fontSize: isMobile ? 10 : 11,
            color: '#666',
            interval: 0,
            formatter: (value: string) => wrapLabel(value, isMobile ? 12 : 14),
            lineHeight: isMobile ? 12 : 14,
          },
        },
        yAxis: {
          type: 'value',
          min: Math.max(0, minValue - padding),
          max: maxValue + padding,
          axisLine: {
            show: true,
            lineStyle: {
              color: '#e0e0e0',
            },
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            fontSize: isMobile ? 10 : 12,
            color: '#666',
            formatter: (value: number) => `${(value / 1000000).toFixed(1)}M`,
          },
          splitLine: {
            lineStyle: {
              type: 'dashed',
              color: '#f0f0f0',
            },
          },
        },
        tooltip: {
          trigger: 'item',
          confine: true,
          formatter: (params: any) => {
            const item = data[params.dataIndex]
            const colorBg = `${item.color}0F`
            const colorLight = `${item.color}B3`

            let html = `<div style="padding: 4px; font-family: Inter, system-ui, sans-serif;">
            <div style="font-size: 14px; font-weight: 700; color: #1a1a1a; margin-bottom: 8px;">${item.name}</div>
            <div style="background: ${colorBg}; border-radius: 8px; padding: 10px 12px; margin-bottom: 8px;">
              <div style="font-size: 20px; font-weight: 700; color: ${item.color};">${formatCurrency(item.value, currency)}</div>`

            if (showPricePerSqm && item.area && item.area > 0) {
              const pricePerSqm = Math.round(item.value / item.area)
              html += `<div style="font-size: 13px; font-weight: 500; color: ${colorLight}; margin-top: 2px;">${formatCurrency(pricePerSqm, currency)} /m²</div>`
            }

            html += `</div>`

            if (item.confidenceLow && item.confidenceHigh) {
              html += `<div style="background: #f8f8f8; border-radius: 8px; padding: 10px 12px;">
              <div style="font-size: 11px; font-weight: 600; color: #bbb; letter-spacing: 0.5px; text-transform: uppercase; margin-bottom: 6px;">${rangeLabel || t('valuations.chart.range')}</div>
              <div style="margin-bottom: 4px;">
                <div style="font-size: 10px; font-weight: 500; color: #aaa;">▼ Min</div>
                <div style="display: flex; align-items: baseline; gap: 6px;">
                  <span style="font-size: 14px; font-weight: 600; color: #555;">${formatCurrency(item.confidenceLow, currency)}</span>`

              if (showPricePerSqm && item.area && item.area > 0) {
                const lowPerSqm = Math.round(item.confidenceLow / item.area)
                html += `<span style="font-size: 12px; color: #999;">· ${formatCurrency(lowPerSqm, currency)} /m²</span>`
              }

              html += `</div></div>
              <div>
                <div style="font-size: 10px; font-weight: 500; color: #aaa;">▲ Max</div>
                <div style="display: flex; align-items: baseline; gap: 6px;">
                  <span style="font-size: 14px; font-weight: 600; color: #555;">${formatCurrency(item.confidenceHigh, currency)}</span>`

              if (showPricePerSqm && item.area && item.area > 0) {
                const highPerSqm = Math.round(item.confidenceHigh / item.area)
                html += `<span style="font-size: 12px; color: #999;">· ${formatCurrency(highPerSqm, currency)} /m²</span>`
              }

              html += `</div></div></div>`
            }

            html += '</div>'
            return html
          },
          backgroundColor: '#fff',
          borderColor: '#e8e8e8',
          borderWidth: 1,
          borderRadius: 10,
          padding: isMobile ? 8 : 12,
          textStyle: {
            color: '#333',
            fontSize: isMobile ? 12 : 14,
          },
        },
        series: [
          // Range lines (custom graphic)
          {
            type: 'custom',
            renderItem: (params: any, api: any) => {
              const item = data[params.dataIndex]
              if (!item.confidenceLow || !item.confidenceHigh) return null

              const xValue = api.value(0)
              const yHigh = api.coord([xValue, item.confidenceHigh])
              const yLow = api.coord([xValue, item.confidenceLow])
              const x = api.coord([xValue, item.value])[0]

              const optimistText = formatCurrency(item.confidenceHigh, currency)
              const pessimistText = formatCurrency(item.confidenceLow, currency)
              const labelSize = isMobile ? 9 : 11

              const children = [
                // Vertical line (range bar)
                {
                  type: 'line',
                  shape: {
                    x1: x,
                    y1: yHigh[1],
                    x2: x,
                    y2: yLow[1],
                  },
                  style: {
                    stroke: item.color,
                    lineWidth: isMobile ? 4 : 6,
                    lineCap: 'round',
                    opacity: 0.3,
                  },
                },
                // Optimist label (top)
                {
                  type: 'text',
                  x: x,
                  y: yHigh[1] - (isMobile ? 16 : 22),
                  style: {
                    text: optimistText,
                    fontSize: labelSize,
                    fill: item.color,
                    textAlign: 'center',
                    fontWeight: 500,
                  },
                },
                // Pessimist label (bottom)
                {
                  type: 'text',
                  x: x,
                  y: yLow[1] + (isMobile ? 10 : 12),
                  style: {
                    text: pessimistText,
                    fontSize: labelSize,
                    fill: item.color,
                    textAlign: 'center',
                    fontWeight: 500,
                  },
                },
              ]

              return {
                type: 'group',
                children,
              }
            },
            data: data.map((item, index) => [index, item.value]),
            z: 1,
          },
          // Final valuation reference lines (custom render behind everything)
          ...(finalValuation?.value
            ? [
                {
                  type: 'custom',
                  renderItem: (_params: any, api: any) => {
                    // Get grid bounds using api.getWidth/getHeight and grid config
                    const gridLeft = isMobile ? 40 : 60
                    const gridRight = isMobile
                      ? 16
                      : finalValuation?.value
                        ? 120
                        : 30
                    const chartWidth = api.getWidth()
                    const gridX = gridLeft
                    const gridWidth = chartWidth - gridLeft - gridRight

                    const children: any[] = []

                    // Helper to create a horizontal line
                    const createLine = (
                      yValue: number,
                      isDashed: boolean,
                      showLabel: boolean
                    ) => {
                      const yPixel = api.coord([0, yValue])[1]
                      const lineElements: any[] = [
                        {
                          type: 'line',
                          shape: {
                            x1: gridX,
                            y1: yPixel,
                            x2: gridX + gridWidth,
                            y2: yPixel,
                          },
                          style: {
                            stroke: primaryColor,
                            lineWidth: isDashed ? 1 : 2,
                            opacity: isDashed ? 0.5 : 1,
                            ...(isDashed ? { lineDash: [6, 4] } : {}), // Only add lineDash for dashed lines
                          },
                        },
                      ]

                      // Add label for main value line (show formatted price) - hide on mobile
                      if (showLabel && !isMobile) {
                        lineElements.push({
                          type: 'text',
                          x: gridX + gridWidth + 5,
                          y: yPixel,
                          style: {
                            text: formatCurrency(yValue, currency),
                            fontSize: 11,
                            fill: primaryColor,
                            fontWeight: 600,
                            textVerticalAlign: 'middle',
                          },
                        })
                      }

                      return lineElements
                    }

                    // Main value line (solid)
                    if (finalValuation.value) {
                      children.push(
                        ...createLine(finalValuation.value, false, true)
                      )
                    }

                    // Range min line (dashed)
                    if (finalValuation.range?.min) {
                      children.push(
                        ...createLine(finalValuation.range.min, true, false)
                      )
                    }

                    // Range max line (dashed)
                    if (finalValuation.range?.max) {
                      children.push(
                        ...createLine(finalValuation.range.max, true, false)
                      )
                    }

                    return {
                      type: 'group',
                      children,
                    }
                  },
                  data: [0], // Single item to trigger renderItem once
                  z: 0, // Render above grid lines but below data points (z:1) and scatter (z:2)
                  silent: true,
                },
              ]
            : []),
          // Value dots (scatter)
          {
            type: 'scatter',
            data: data.map((item, index) => ({
              value: [index, item.value],
              itemStyle: { color: item.color },
              label: {
                show: !isMobile,
                position: 'right',
                formatter: formatCurrency(item.value, currency),
                fontSize: 12,
                fontWeight: 600,
                color: item.color,
                offset: [10, 0],
              },
            })),
            symbolSize: isMobile ? 10 : 12,
            z: 2,
          },
        ],
      }
    }, [
      data,
      currency,
      finalValuation,
      finalValuationLabel,
      primaryColor,
      t,
      isMobile,
      showPricePerSqm,
    ])

    const chartHeight = typeof height === 'number' ? `${height}px` : height

    return (
      <ReactECharts
        option={option}
        style={{ height: chartHeight, width: '100%' }}
        opts={{ renderer: 'svg' }}
        onChartReady={(chart) => {
          chartInstance.current = chart
        }}
      />
    )
  }
)

ValuationComparisonChart.displayName = 'ValuationComparisonChart'
